import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldAlert, UserPlus, ShieldCheck, UserMinus, Pencil, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getStaff, getAdminArtists, setAdminAccess } from "@/lib/afmApi";
import { Loading, SectionTitle } from "@/components/common";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black";

const PERMISSIONS = [
  { key: "accounting", label: "Contabilidad" },
  { key: "insights", label: "Insights" },
  { key: "catalogo", label: "Catálogo" },
  { key: "herramientas", label: "Herramientas" },
];
const permLabel = (k) => PERMISSIONS.find((p) => p.key === k)?.label || k;

const PermChecks = ({ selected, onToggle, idPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {PERMISSIONS.map((p) => {
      const on = selected.includes(p.key);
      return (
        <button
          key={p.key}
          type="button"
          data-testid={`${idPrefix}-${p.key}`}
          onClick={() => onToggle(p.key)}
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-black bg-black text-white" : "border-slate-300 text-slate-600 hover:border-black"}`}
        >
          {on && <Check className="h-3 w-3" />} {p.label}
        </button>
      );
    })}
  </div>
);

export default function AdminStaff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [artists, setArtists] = useState([]);
  const [status, setStatus] = useState("loading");
  const [msg, setMsg] = useState("");
  const [target, setTarget] = useState("");
  const [grantPerms, setGrantPerms] = useState(PERMISSIONS.map((p) => p.key));
  const [busy, setBusy] = useState("");
  const [editId, setEditId] = useState("");
  const [editPerms, setEditPerms] = useState([]);

  const canManage = !!user?.can_manage_admins || !!user?.super_admin;

  const load = useCallback(() => {
    setStatus("loading");
    Promise.all([getStaff(user.id), getAdminArtists(user.id).catch(() => ({ artists: [] }))])
      .then(([s, a]) => { setStaff(s?.staff || []); setArtists(a?.artists || []); setStatus("ok"); })
      .catch((e) => { setMsg(e?.response?.data?.detail || "Acceso restringido"); setStatus("denied"); });
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    if (!target) { toast.error("Elige un usuario"); return; }
    setBusy(target);
    try { await setAdminAccess({ viewer_id: user.id, target_user_id: target, grant: true, permissions: grantPerms }); toast.success("Acceso admin otorgado"); setTarget(""); setGrantPerms(PERMISSIONS.map((p) => p.key)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo otorgar"); }
    finally { setBusy(""); }
  };

  const revoke = async (id) => {
    setBusy(id);
    try { await setAdminAccess({ viewer_id: user.id, target_user_id: id, grant: false }); toast.success("Acceso admin removido"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo remover"); }
    finally { setBusy(""); }
  };

  const openEdit = (s) => { setEditId(s.id); setEditPerms(Array.isArray(s.permissions) ? s.permissions : []); };
  const saveEdit = async (id) => {
    setBusy(id);
    try { await setAdminAccess({ viewer_id: user.id, target_user_id: id, grant: true, permissions: editPerms }); toast.success("Permisos actualizados"); setEditId(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
    finally { setBusy(""); }
  };

  if (status === "loading") return <Loading />;
  if (status === "denied") return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">{msg}</p>
    </div>
  );

  const staffIds = new Set(staff.map((s) => s.id));
  const grantable = artists.filter((a) => !staffIds.has(a.user_id));
  const toggle = (arr, set, k) => set(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-staff-page">
      <SectionTitle>Equipo & Acceso</SectionTitle>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-800">
        Los permisos por módulo se guardan en cada miembro y el backend ya aplica el bloqueo real por módulo: cada endpoint admin exige el permiso correspondiente (403 si falta).
      </div>

      {canManage && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-black"><UserPlus className="h-4 w-4 text-[#AC3333]" /> Otorgar acceso admin</div>
          <div className="flex gap-2">
            <select data-testid="grant-target" value={target} onChange={(e) => setTarget(e.target.value)} className={inputCls}>
              <option value="">Selecciona un artista…</option>
              {grantable.map((a) => <option key={a.user_id} value={a.user_id}>{a.name} · {a.email}</option>)}
            </select>
            <button data-testid="grant-access" onClick={grant} disabled={!target || busy} className="shrink-0 rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">Dar acceso</button>
          </div>
          <div className="mt-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Módulos que podrá gestionar</div>
            <PermChecks selected={grantPerms} onToggle={(k) => toggle(grantPerms, setGrantPerms, k)} idPrefix="grant-perm" />
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4"><h2 className="font-serif text-lg font-bold text-black">Administradores</h2></div>
        <div className="divide-y divide-slate-100">
          {staff.map((s) => (
            <div key={s.id} className="px-5 py-4" data-testid={`staff-row-${s.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-xs font-bold text-white">{(s.name || s.email || "?").charAt(0).toUpperCase()}</div>
                  <div>
                    <div className="flex items-center gap-2 font-medium text-black">{s.name} {s.super_admin && <ShieldCheck className="h-4 w-4 text-emerald-600" title="Super admin" />}</div>
                    <div className="text-xs text-slate-500">{s.email} · {s.super_admin ? "Super Admin" : "Admin distribución"}</div>
                  </div>
                </div>
                {canManage && !s.super_admin && s.id !== user.id && (
                  <div className="flex items-center gap-2">
                    {editId === s.id ? (
                      <>
                        <button data-testid={`save-perms-${s.id}`} onClick={() => saveEdit(s.id)} disabled={busy === s.id} className="flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Guardar</button>
                        <button onClick={() => setEditId("")} className="rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:border-black"><X className="h-3.5 w-3.5" /></button>
                      </>
                    ) : (
                      <>
                        <button data-testid={`edit-perms-${s.id}`} onClick={() => openEdit(s)} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:border-black"><Pencil className="h-3.5 w-3.5" /> Permisos</button>
                        <button data-testid={`revoke-${s.id}`} onClick={() => revoke(s.id)} disabled={busy === s.id} className="flex items-center gap-1 rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50"><UserMinus className="h-3.5 w-3.5" /> Quitar</button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {!s.super_admin && (
                <div className="mt-3 pl-12">
                  {editId === s.id ? (
                    <PermChecks selected={editPerms} onToggle={(k) => toggle(editPerms, setEditPerms, k)} idPrefix={`edit-perm-${s.id}`} />
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(s.permissions || []).length > 0 ? (s.permissions || []).map((p) => (
                        <span key={p} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600">{permLabel(p)}</span>
                      )) : <span className="text-[11px] text-slate-400">Sin permisos por módulo asignados</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {!staff.length && <div className="px-5 py-10 text-center text-sm text-slate-400">Sin administradores.</div>}
        </div>
      </div>
      {!canManage && <p className="text-xs text-slate-400">Solo un Super Admin puede otorgar o quitar accesos.</p>}
    </div>
  );
}
